<!-- C:\MyVueApps\sentinel-safe\src/components/client-admin/ClientFeatureRequestsList.vue -->
<template>
  <div class="bg-white p-6 rounded-lg shadow-xl">
    <h2 class="text-xl font-semibold text-brand-primary mb-4 border-b pb-2">Submitted Requests</h2>
    
    <div v-if="loading" class="text-center py-4">
      <p class="text-gray-500">Loading submitted requests...</p>
    </div>
    <div v-else-if="error" class="text-center py-4">
      <p class="text-red-500">{{ error }}</p>
    </div>
    <div v-else-if="featureRequests.length === 0" class="text-center py-4">
      <p class="text-gray-500">No feature requests have been submitted for this client yet.</p>
    </div>

    <div v-else class="space-y-3 max-h-96 overflow-y-auto">
      <!-- DEFINITIVE FIX: Each item is now a clickable router-link that navigates to the detail page -->
      <router-link 
        v-for="request in featureRequests" 
        :key="request.id" 
        :to="{ name: 'AdminFeatureRequestDetail', params: { requestId: request.id } }"
        class="block p-3 rounded-md bg-gray-50 border hover:bg-gray-100 transition-colors"
      >
        <div class="flex items-start justify-between">
          <div>
            <p class="text-xs text-gray-500">
              Requested by {{ request.requesterName }} on {{ formatDate(request.createdAt) }}
            </p>
            <p class="mt-2 text-sm text-gray-700">{{ request.details }}</p>
          </div>
          
          <div class="flex items-center flex-shrink-0 ml-4">
             <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize" :class="statusClass(request.status)">
              {{ request.status.replace('-', ' ') }}
            </span>
          </div>
        </div>
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch } from 'vue';
import { db } from '@/firebaseConfig';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { useUserStore } from '@/stores/userStore';

const userStore = useUserStore();
const loading = ref(true);
const error = ref(null);
const featureRequests = ref([]);

let unsubscribe = null;

const formatDate = (timestamp) => {
  if (!timestamp || !timestamp.seconds) return 'N/A';
  return new Date(timestamp.seconds * 1000).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric'
  });
};

const statusClass = (status) => {
  switch (status) {
    case 'new':
      return 'bg-blue-100 text-blue-800';
    case 'under-review':
      return 'bg-yellow-100 text-yellow-800';
    case 'planned':
      return 'bg-purple-100 text-purple-800';
    case 'complete':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const setupListener = (clientId) => {
    if (!clientId) {
        error.value = "Client ID not found. Cannot load requests.";
        loading.value = false;
        return;
    }
    const requestsRef = collection(db, 'featureRequests');
    const q = query(
      requestsRef, 
      where('clientId', '==', clientId),
      orderBy('createdAt', 'desc')
    );

    unsubscribe = onSnapshot(q, (snapshot) => {
      featureRequests.value = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      loading.value = false;
    }, (err) => {
      console.error("Error fetching feature requests:", err);
      error.value = "Failed to load feature requests.";
      loading.value = false;
    });
};

onMounted(() => {
  if (userStore.claims?.clientId) {
    setupListener(userStore.claims.clientId);
  } else {
    const unwatch = watch(() => userStore.claims, (newClaims) => {
        if (newClaims?.clientId) {
            setupListener(newClaims.clientId);
            unwatch();
        }
    }, { immediate: true });

    setTimeout(() => {
        if (loading.value && !unsubscribe) {
            error.value = "Could not verify client ID. Unable to load requests.";
            loading.value = false;
        }
    }, 5000);
  }
});

onUnmounted(() => {
  if (unsubscribe) {
    unsubscribe();
  }
});
</script>